/**
 * Tiny browser-side buffer shared by Next.js client instrumentation and the
 * hydrated React diagnostics/analytics providers.
 *
 * Keep this module dependency-free: `instrumentation-client.ts` runs before
 * the application hydrates, so anything imported here is part of the earliest
 * client bootstrap path.
 */

import { isIgnorableClientError } from "./benign-browser-notifications";

export type ClientNavigationType = "push" | "replace" | "traverse";

export interface BufferedClientError {
  kind: "error" | "unhandledrejection";
  message: string;
  name: string;
  stack?: string;
  source?: string;
  line?: number;
  column?: number;
  capturedAt: number;
}

export interface BufferedRouterTransition {
  url: string;
  destination: string;
  navigationType: ClientNavigationType;
  startedAt: number;
  startedAtEpochMs: number;
}

interface ClientInstrumentationState {
  errors: BufferedClientError[];
  analyticsErrors: BufferedClientError[];
  transitions: BufferedRouterTransition[];
  errorCaptureInstalled: boolean;
  onError?: (event: ErrorEvent) => void;
  onUnhandledRejection?: (event: PromiseRejectionEvent) => void;
}

declare global {
  interface Window {
    __LOOPDESK_CLIENT_INSTRUMENTATION__?: ClientInstrumentationState;
  }
}

const MAX_BUFFERED_ERRORS = 20;
const MAX_BUFFERED_TRANSITIONS = 10;
const MAX_TRANSITION_AGE_MS = 15_000;

function getState(): ClientInstrumentationState | null {
  if (typeof window === "undefined") return null;

  window.__LOOPDESK_CLIENT_INSTRUMENTATION__ ??= {
    errors: [],
    analyticsErrors: [],
    transitions: [],
    errorCaptureInstalled: false,
  };

  // Preserve state across Fast Refresh or a rolling deployment where an older
  // bundle created the shared object before the analytics queue existed.
  window.__LOOPDESK_CLIENT_INSTRUMENTATION__.analyticsErrors ??= [];

  return window.__LOOPDESK_CLIENT_INSTRUMENTATION__;
}

function trimToLimit<T>(items: T[], limit: number): void {
  if (items.length > limit) {
    items.splice(0, items.length - limit);
  }
}

function safeString(value: unknown, fallback: string): string {
  try {
    if (typeof value === "string" && value) return value;
    if (value instanceof Error) return value.message || value.name || fallback;
    if (value != null) return String(value);
  } catch {
    // Some host objects/proxies throw while being stringified.
  }
  return fallback;
}

function toBufferedError(
  kind: BufferedClientError["kind"],
  value: unknown,
  location?: Pick<ErrorEvent, "filename" | "lineno" | "colno" | "message">,
): BufferedClientError {
  const error = value instanceof Error ? value : null;

  return {
    kind,
    message:
      error?.message ||
      location?.message ||
      safeString(
        value,
        kind === "error" ? "Unknown error" : "Unknown rejection",
      ),
    name: error?.name || (kind === "error" ? "Error" : "UnhandledRejection"),
    stack: error?.stack,
    source: location?.filename || undefined,
    line: location?.lineno || undefined,
    column: location?.colno || undefined,
    capturedAt: Date.now(),
  };
}

/** Install bounded global listeners during the pre-hydration window. */
export function installEarlyClientErrorCapture(): void {
  const state = getState();
  if (!state || state.errorCaptureInstalled) return;

  state.onError = (event) => {
    // Benign browser notifications (e.g. the ResizeObserver loop notice) and
    // recoverable React hydration mismatches (browser-extension DOM mutation)
    // are self-correcting and not application faults. Dropping them here keeps
    // them out of the buffer that is later replayed into diagnostics and error
    // tracking.
    if (isIgnorableClientError(event.error?.message || event.message)) {
      return;
    }

    const buffered = toBufferedError("error", event.error, event);
    state.errors.push(buffered);
    state.analyticsErrors.push({ ...buffered });
    trimToLimit(state.errors, MAX_BUFFERED_ERRORS);
    trimToLimit(state.analyticsErrors, MAX_BUFFERED_ERRORS);
  };

  state.onUnhandledRejection = (event) => {
    const buffered = toBufferedError("unhandledrejection", event.reason);
    state.errors.push(buffered);
    state.analyticsErrors.push({ ...buffered });
    trimToLimit(state.errors, MAX_BUFFERED_ERRORS);
    trimToLimit(state.analyticsErrors, MAX_BUFFERED_ERRORS);
  };

  window.addEventListener("error", state.onError);
  window.addEventListener("unhandledrejection", state.onUnhandledRejection);
  state.errorCaptureInstalled = true;
}

/**
 * Remove only the bootstrap listeners. Hydrated consumers install their own
 * long-lived handlers before calling this, so no error-capture gap is created.
 */
export function stopEarlyClientErrorCapture(): void {
  const state = getState();
  if (!state?.errorCaptureInstalled) return;

  if (state.onError) window.removeEventListener("error", state.onError);
  if (state.onUnhandledRejection) {
    window.removeEventListener(
      "unhandledrejection",
      state.onUnhandledRejection,
    );
  }

  state.onError = undefined;
  state.onUnhandledRejection = undefined;
  state.errorCaptureInstalled = false;
}

/** Return and clear errors captured before the React diagnostics mounted. */
export function drainBufferedClientErrors(): BufferedClientError[] {
  const state = getState();
  return state ? state.errors.splice(0, state.errors.length) : [];
}

/**
 * Return and clear the independent analytics copy of pre-hydration errors.
 * This queue is intentionally separate from the diagnostics/UI queue because
 * PostHog may not be initialized when the hydrated diagnostics first mount.
 */
export function drainBufferedClientAnalyticsErrors(): BufferedClientError[] {
  const state = getState();
  return state
    ? state.analyticsErrors.splice(0, state.analyticsErrors.length)
    : [];
}

function monotonicNow(): number {
  if (typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }
  return Date.now();
}

function normalizeDestination(url: string): string {
  try {
    const resolved = new URL(url, window.location.href);
    // The completion consumer observes usePathname/useSearchParams, neither of
    // which changes for a hash-only navigation.
    return `${resolved.pathname}${resolved.search}`;
  } catch {
    return url;
  }
}

/** Record the earliest point Next exposes for a client-side transition. */
export function recordRouterTransitionStart(
  url: string,
  navigationType: ClientNavigationType,
): void {
  const state = getState();
  if (!state) return;

  const destination = normalizeDestination(url);
  const currentDestination = `${window.location.pathname}${window.location.search}`;

  // Next invokes the hook for same-URL and hash-only pushes, but those do not
  // change the pathname/search values that complete the timing measurement.
  // Do not enqueue a start that cannot have a matching completion effect.
  if (destination === currentDestination) return;

  state.transitions.push({
    url,
    destination,
    navigationType,
    startedAt: monotonicNow(),
    startedAtEpochMs: Date.now(),
  });
  trimToLimit(state.transitions, MAX_BUFFERED_TRANSITIONS);
}

/**
 * Consume the newest transition matching the rendered URL. Older entries are
 * discarded as redirect/interruption predecessors of the completed route.
 */
export function consumeRouterTransition(
  renderedUrl: string,
): (BufferedRouterTransition & { durationMs: number }) | null {
  const state = getState();
  if (!state || state.transitions.length === 0) return null;

  const completedAt = monotonicNow();

  // Hash-only/same-URL navigations and interrupted transitions do not always
  // cause the pathname/search consumer to run. Expire those starts so a later
  // server-action redirect cannot inherit an arbitrarily old duration.
  for (let index = state.transitions.length - 1; index >= 0; index -= 1) {
    const transition = state.transitions[index];
    if (
      transition &&
      completedAt - transition.startedAt > MAX_TRANSITION_AGE_MS
    ) {
      state.transitions.splice(index, 1);
    }
  }
  if (state.transitions.length === 0) return null;

  const destination = normalizeDestination(renderedUrl);
  let matchIndex = -1;
  for (let index = state.transitions.length - 1; index >= 0; index -= 1) {
    if (state.transitions[index]?.destination === destination) {
      matchIndex = index;
      break;
    }
  }

  // A redirect can change the final destination without exposing another
  // transition-start callback. In that case the newest start is still the
  // most useful timing origin.
  if (matchIndex < 0) matchIndex = state.transitions.length - 1;

  const transition = state.transitions[matchIndex];
  state.transitions.splice(0, matchIndex + 1);
  if (!transition) return null;

  return {
    ...transition,
    durationMs: Math.max(0, completedAt - transition.startedAt),
  };
}
